import {Howl} from 'howler';

// チャプターごとの Howl オブジェクトの配列
let trackSounds = [];

const WINDOW_SIZE = {
  width: '418px',
  height: `${Math.min(714, window.innerHeight)}px`
};

const state = {
  playId: null, // AudioContents._id
  index: 0,
  loaded: false,
  loading: false,
  playing: false,
  rate: 1.0,
  seek: 0,
  duration: 0
};

const getters = {
  // 現在再生されている AudioContents データ
  playingContent(state, getters, rootState, rootGetters) {
    if (state.playId) {
      return rootGetters['audioContents/findById'](state.playId);
    }
    return null;
  },
  // 再生中のAudioContents._id
  playId(state) {
    return state.playId;
  },
  // 現在選択しているチャプターデータ
  currentChapter(state, getters) {
    if (getters.playingContent && getters.playingContent.chapters) {
      return getters.playingContent.chapters[getters.index];
    }
    return null;
  },
  // 現在選択しているチャプターインデックス
  index(state) {
    return state.index >= 0 ? state.index : 0;
  },
  // 再生中かどうか
  playing(state) {
    return state.playing;
  },
  // ファイルが読み込み完了しているか
  loaded(state) {
    return state.loaded;
  },
  // ファイルが読み込み中かどうか
  loading(state) {
    return state.loading;
  },
  // play 中の seek データ（秒数）
  seek(state) {
    return state.seek;
  },
  // 現在のチャプターの再生時間
  duration(state, getters) {
    return (
      state.duration ||
      (getters.currentChapter && getters.currentChapter.play_time) ||
      0
    );
  },
  // 再生速度
  rate(state) {
    return state.rate;
  },

  // Desktop window size
  windowSize() {
    return WINDOW_SIZE;
  }
};

const actions = {
  // AudioContents ID の設定
  setPlayId({commit, getters}, payload) {
    if (getters.playId === payload._id) {
      return;
    }

    commit('playId', payload);
  },
  // Howlオブジェクトの生成 params: AudioContents
  setSound({commit, dispatch, getters}, payload) {
    if (!getters.playingContent) {
      return;
    }
    if (!trackSounds[payload.index] || (payload.sample && !trackSounds['sample'])) {
      commit('loaded', false);
      let playDaata;
      if (payload.sample) {
        payload.index = 'sample';
        playDaata = getters.playingContent.sample;
      } else {
        if (!getters.playingContent.chapters[payload.index]) {
          payload.index = 0;
        }
        playDaata = getters.playingContent.chapters[payload.index];
      }
      const sound = new Howl({
        src: [playDaata.source],
        html5: true,
        rate: state.rate,
        onplay: () => {
          commit('playing', true);
          dispatch('step');
        },
        onload: () => {
          commit('loaded', true);
        },
        onpause: () => {
          commit('playing', false);
          dispatch('progress');
        },
        onstop: () => {
          commit('playing', false);
          dispatch('progress');
        },
        onend: () => {
          if (payload.sample) {
            dispatch('stop');
          } else {
            dispatch('skip', {direction: 'next'});
          }
        }
      });
      dispatch('duration', {
        duration: playDaata.play_time
      });
      commit('setSound', {
        index: payload.index,
        sound
      });
      return sound;
    }
  },
  // 再生（Howlオブジェクトの生成）
  play({commit}) {
    commit('play');
  },
  // 一時停止
  pause({commit, dispatch}) {
    commit('pause');
    dispatch('progress');
  },
  // 停止
  stop({commit}) {
    commit('stop');
  },
  progress({state, dispatch}) {
    if (state.index === 'sample') {
      return;
    }
    const data = {
      _id: state.playId,
      chapter_index: state.index,
      seek: state.seek,
      rate: state.rate
    };
    dispatch('audioContents/postProgress', data, {root: true});
  },
  duration({commit}, payload) {
    commit('duration', payload.duration);
  },
  // シークバーの設定（パーセント）
  seek({commit}, payload) {
    commit('seek', payload.per);
  },
  // 前後のチャプターへの移動（direction: prev or next）
  skip({state, getters, dispatch}, payload) {
    const direction = payload.direction;
    let index = 0;
    let autoplay = true;
    if (direction === 'prev') {
      index = state.index - 1;
      if (index < 0) {
        index = 0;
        autoplay = false;
      }
    } else if (direction === 'next') {
      index = state.index + 1;
      if (index >= getters.playingContent.chapters.length) {
        index = 0;
        autoplay = false;
      }
    }
    dispatch('skipTo', {index, autoplay});
  },
  // 指定したインデックスのチャプターに移動。stop オプションを指定しない限り自動再生
  skipTo({dispatch, commit}, payload) {
    commit('index', payload.index);
    dispatch('setSound', {index: payload.index});
    dispatch('seek', {per: 0});
    if (!payload.stop) {
      dispatch('play');
    }
  },
  // 再生速度の設定
  rateTo({commit, dispatch}, payload) {
    commit('rate', payload.rate);
    dispatch('progress');
  },
  // シーキングの管理。再生中は描画ごとに常に呼び出される
  step({dispatch, commit, getters}) {
    commit('step');
    if (getters.playing) {
      requestAnimationFrame(() => dispatch('step'));
    }
  }
};

const mutations = {
  // AudioContents の登録
  playId(state, payload) {
    if (state.playId === payload._id) {
      return;
    }
    for (const sound of trackSounds) {
      if (sound) {
        sound.unload();
      }
    }
    trackSounds = [];
    state.playId = payload._id;
  },
  // チャプターインデックスの変更
  index(state, index) {
    if (trackSounds[state.index]) {
      trackSounds[state.index].stop();
    }
    state.index = index;
  },
  // 再生中フラグの変更
  playing(state, playing) {
    state.playing = playing;
  },
  // Howl オブジェクトデータの登録
  setSound(state, payload) {
    if (trackSounds[state.index]) {
      trackSounds[state.index].unload();
    }
    state.index = payload.index;
    trackSounds[state.index] = payload.sound;
  },
  // 再生ファイルのロードフラグの変更
  loaded(state, bool) {
    state.loaded = bool;
    state.loading = !bool;
  },
  // 再生
  play(state) {
    trackSounds[state.index].play();
  },
  // 一時停止
  pause(state) {
    trackSounds[state.index].pause();
  },
  // 停止
  stop(state) {
    trackSounds[state.index].stop();
  },
  duration(state, time) {
    state.duration = time;
  },
  // シークバーの変更
  seek(state, per) {
    state.seek = state.duration * per;
    trackSounds[state.index].seek(state.seek);
  },
  // 再生速度の変更
  rate(state, rate) {
    state.rate = rate;
    trackSounds[state.index].rate(rate);
  },
  // 現在の再生時間の取得
  step(state) {
    state.seek = trackSounds[state.index].seek();
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
